import { availableRechargeMethods, initialRechargeTab } from '@/utils/rechargeAvailability';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchHomeDisplayConfig, type MemberPaymentOptions, type MemberPaymentTicketResult, type MemberRechargeCreateResult, type MemberRechargeQueryResult } from '@/api/member';
import { isAndroidNativeAppRuntime } from '@/utils/nativeApp';
import { FirstRechargeBonusBanner, FirstRechargeCreditPreview } from './FirstRechargeBonus';
import { useFirstRechargePreview } from './useFirstRechargePreview';

type PayMethod = 'alipay' | 'wechat' | 'usdt';
type RechargeTab = 'cny' | 'usdt';
const fallbackAppDownloadUrl = 'https://img.mfantasy.net/app/vod-0824a.apk';

interface Props {
  open: boolean;
  onClose: () => void;
  onRechargeSuccess?: (points: number) => void;
  loadOptions?: () => Promise<MemberPaymentOptions>;
  createTicket?: (packageId: string, payMethod: PayMethod) => Promise<MemberPaymentTicketResult>;
  createOrder?: (packageId: string, payMethod: PayMethod) => Promise<MemberRechargeCreateResult>;
  queryOrder?: (outTradeNo: string) => Promise<MemberRechargeQueryResult>;
}

function amountText(amount: number, currency: string) {
  return currency === 'USDT' ? `${(amount / 100).toFixed(2)} USDT` : `¥${(amount / 100).toFixed(2)}`;
}

function sorted<T extends { sort: number; id: string }>(rows: T[]) {
  return [...rows].sort((a, b) => (Number(a.sort) - Number(b.sort)) || String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
}

export default function CinemaRechargeModal({ open, onClose, onRechargeSuccess, loadOptions, createTicket, createOrder, queryOrder }: Props) {
  const { t } = useTranslation('cinema');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<MemberPaymentOptions | null>(null);
  const [tab, setTab] = useState<RechargeTab>('cny');
  const [selectedId, setSelectedId] = useState('');
  const [payMethod, setPayMethod] = useState<PayMethod | ''>('');
  const [order, setOrder] = useState<MemberRechargeCreateResult | null>(null);
  const [ticket, setTicket] = useState<MemberPaymentTicketResult | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [pendingOutTradeNo, setPendingOutTradeNo] = useState('');
  const [result, setResult] = useState<MemberRechargeQueryResult | null>(null);
  const [message, setMessage] = useState('');
  const [appPromptOpen, setAppPromptOpen] = useState(false);
  const [appDownloadUrl, setAppDownloadUrl] = useState('');
  const [appLoginRewardPoints, setAppLoginRewardPoints] = useState(100);
  const [paymentGuideOpen, setPaymentGuideOpen] = useState(false);
  const [rechargeBonusRemainingSeconds, setRechargeBonusRemainingSeconds] = useState(0);
  const createTicketRef = useRef(createTicket);
  const rechargeSucceededRef = useRef(false);
  const paymentGuideShownRef = useRef(false);

  useEffect(() => {
    createTicketRef.current = createTicket;
  }, [createTicket]);

  const hasCny = availableRechargeMethods(options, 'cny').length > 0;
  const hasUsdt = availableRechargeMethods(options, 'usdt').length > 0;
  const hasPayments = hasCny || hasUsdt;
  const packages = useMemo(() => sorted(tab === 'usdt' ? options?.usdtPackages ?? [] : options?.packages ?? []), [options, tab]);
  const selected = useMemo(() => packages.find((item) => item.id === selectedId) ?? null, [packages, selectedId]);
  const methods = useMemo(() => availableRechargeMethods(options, tab), [options, tab]);
  const selectedMethod = useMemo(() => methods.find((item) => item.id === payMethod) ?? null, [methods, payMethod]);
  const packageAvailable = (packageId: string) => Boolean(selectedMethod && (selectedMethod.availablePackageIds ?? []).includes(packageId));

  useEffect(() => {
    if (!selectedMethod || packageAvailable(selectedId)) return;
    setSelectedId(packages.find((item) => (selectedMethod.availablePackageIds ?? []).includes(item.id))?.id ?? '');
  }, [selectedMethod, selectedId, packages]);
  const rechargeBonusType = options?.rechargeBonusType || (options?.firstRechargeEligible ? 'first' : '');
  const rawRechargeBonusPercent = Number(options?.rechargeBonusPercent ?? options?.firstRechargeBonusPercent ?? 20);
  const rechargeBonusPercent = Number.isFinite(rawRechargeBonusPercent) ? Math.max(0, rawRechargeBonusPercent) : 20;
  const rechargeBonusActive = Boolean((options?.rechargeBonusEligible ?? options?.firstRechargeEligible) && rechargeBonusRemainingSeconds > 0 && rechargeBonusType);
  const activityBonusMode = Boolean(options?.activityBonusMode);
  const { animatingId: bonusAnimatingId, appliedId: bonusAppliedId } = useFirstRechargePreview(selectedId, rechargeBonusActive && !activityBonusMode);

  const applyDefaults = (data: MemberPaymentOptions, nextTab: RechargeTab) => {
    const rows = sorted(nextTab === 'usdt' ? data.usdtPackages ?? [] : data.packages ?? []);
    const method = availableRechargeMethods(data, nextTab)[0];
    const availableRows = rows.filter((item) => method?.availablePackageIds?.includes(item.id));
    setSelectedId((availableRows.find((item) => item.recommended) ?? availableRows[0])?.id ?? '');
    setPayMethod((method?.id as PayMethod | undefined) ?? '');
  };

  useEffect(() => {
    if (!open) {
      setOptions(null); setTab('cny'); setSelectedId(''); setPayMethod('');
      setOrder(null); setTicket(null); setPendingOutTradeNo(''); setResult(null); setMessage(''); setLoading(false); setTicketLoading(false); setPaymentGuideOpen(false);
      setRechargeBonusRemainingSeconds(0);
      paymentGuideShownRef.current = false;
      return;
    }
    let active = true;
    rechargeSucceededRef.current = false;
    setAppPromptOpen(false);
    if (!isAndroidNativeAppRuntime()) {
      void fetchHomeDisplayConfig()
        .then((config) => {
          if (!active) return;
          setAppDownloadUrl((config.appDownloadUrl || '').trim());
          setAppLoginRewardPoints(Math.max(0, Number(config.appLoginRewardPoints) || 0));
        })
        .catch(() => {
          if (!active) return;
          setAppDownloadUrl('');
          setAppLoginRewardPoints(100);
        });
    }
    if (!loadOptions) return () => { active = false; };
    setLoading(true);
    setOptions(null); setTicket(null); setMessage('');
    void loadOptions().then((data) => {
      if (!active) return;
      setOptions(data);
      setRechargeBonusRemainingSeconds(Math.max(0, Number(data.rechargeBonusRemainingSeconds ?? data.firstRechargeBonusRemainingSeconds) || 0));
      const initial: RechargeTab = initialRechargeTab(data);
      setTab(initial);
      applyDefaults(data, initial);
    }).catch((error: unknown) => { if (active) setMessage(error instanceof Error ? error.message : t('cinema_paymentUnavailable')); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadOptions, open, t]);

  useEffect(() => {
    if (!open || !rechargeBonusActive || rechargeBonusRemainingSeconds <= 0) return;
    const timer = window.setInterval(() => setRechargeBonusRemainingSeconds((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [open, rechargeBonusActive, rechargeBonusRemainingSeconds]);

  useEffect(() => {
    if (!open || !options?.enabled || !selectedId || !payMethod || !packageAvailable(selectedId) || !createTicketRef.current) {
      setTicket(null);
      setTicketLoading(false);
      return;
    }
    setTicket(null);
    let active = true;
    const issue = () => {
      setTicketLoading(true);
      void createTicketRef.current?.(selectedId, payMethod)
        .then((next) => {
          if (active) setTicket(next);
        })
        .catch(() => {
          if (active) setTicket(null);
        })
        .finally(() => {
          if (active) setTicketLoading(false);
        });
    };
    issue();
    const refreshTimer = window.setInterval(issue, 4 * 60 * 1000);
    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, [open, options?.enabled, payMethod, selectedId, selectedMethod]);

  useEffect(() => {
    const outTradeNo = pendingOutTradeNo || order?.outTradeNo || '';
    if (!outTradeNo || !queryOrder) return;
    let stopped = false;
    const timer = window.setInterval(() => {
      void queryOrder(outTradeNo).then((next) => {
        if (stopped) return;
        setResult(next);
        if (next.status === 'paid' && next.credited) {
          stopped = true;
          window.clearInterval(timer);
          rechargeSucceededRef.current = true;
          onRechargeSuccess?.(next.points);
          onClose();
        } else if (['failed', 'closed', 'expired'].includes(next.status)) {
          stopped = true;
          window.clearInterval(timer);
        }
      }).catch(() => undefined);
    }, 3000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [onClose, onRechargeSuccess, order?.outTradeNo, pendingOutTradeNo, queryOrder]);

  const closeWithoutRecharge = () => {
    const outTradeNo = pendingOutTradeNo || order?.outTradeNo || '';
    onClose();
    if (rechargeSucceededRef.current || isAndroidNativeAppRuntime()) return;
    if (outTradeNo && queryOrder) {
      void queryOrder(outTradeNo)
        .then((next) => {
          if (next.status === 'paid' && next.credited) {
            rechargeSucceededRef.current = true;
            onRechargeSuccess?.(next.points);
            return;
          }
          setAppPromptOpen(true);
        })
        .catch(() => setAppPromptOpen(true));
      return;
    }
    setAppPromptOpen(true);
  };

  if (!open) {
    if (!appPromptOpen) return null;
    const downloadUrl = appDownloadUrl || fallbackAppDownloadUrl;
    return <div className="cinema-modal-safe fixed inset-0 z-[70] flex items-center justify-center px-4 pt-4 sm:p-5">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setAppPromptOpen(false)} />
      <div className="relative z-10 max-h-[calc(100dvh-5rem)] w-full max-w-sm overflow-y-auto rounded-2xl border border-background-800 bg-background-900 p-5 text-center shadow-2xl sm:max-h-[calc(100dvh-2.5rem)] sm:p-6">
        <button
          type="button"
          onClick={() => setAppPromptOpen(false)}
          aria-label={t('cinema_cancel')}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-foreground-100"
        >
          <i className="ri-close-line text-lg" />
        </button>
        <img
          src="/images/02.webp"
          alt={t('cinema_brand')}
          className="mx-auto h-14 w-14 rounded-2xl object-cover"
        />
        <h2 className="mt-4 text-xl font-bold text-foreground-50">{t('cinema_rechargeAppPromptTitle')}</h2>
        <p className="mt-2 text-sm font-semibold text-accent-400">
          {t('cinema_rechargeAppPromptReward', { points: appLoginRewardPoints })}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-foreground-500">{t('cinema_rechargeAppPromptDesc')}</p>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#F19744] text-sm font-bold text-background-950 hover:bg-[#F19744]/90"
        >
          <i className="ri-download-2-line text-lg" />
          {t('cinema_appDownloadButton')}
        </a>
      </div>
    </div>;
  }

  const changeTab = (next: RechargeTab) => {
    setTab(next);
    if (options) applyDefaults(options, next);
    setOrder(null); setTicket(null); setPendingOutTradeNo(''); setResult(null); setMessage('');
  };

  const pay = () => {
    if (loading || ticketLoading) return;
    if (!options?.enabled || !selected || !payMethod || !packageAvailable(selected.id) || !createOrder || methods.length === 0) {
      setMessage(t('cinema_paymentUnavailable'));
      return;
    }
    setLoading(true); setMessage('');
    void createOrder(selected.id, payMethod).then((next) => {
      setOrder(next); setResult(null);
      setPendingOutTradeNo(next.outTradeNo);
      const target = next.pageUrl || next.payUrl;
      if (target) {
        window.open(target, '_blank', 'noopener,noreferrer');
        setMessage(t('cinema_paymentAwaiting'));
      }
    }).catch((error: unknown) => setMessage(error instanceof Error ? error.message : t('cinema_paymentUnavailable'))).finally(() => setLoading(false));
  };

  const startTicketPayment = () => {
    if (!ticket || !options?.enabled || !selected || !packageAvailable(selected.id) || loading || ticketLoading) return;
    setPendingOutTradeNo(ticket.outTradeNo);
    setMessage(t('cinema_paymentAwaiting'));
    window.open(ticket.launchUrl, '_blank', 'noopener,noreferrer');
  };

  const continuePayment = () => {
    setPaymentGuideOpen(false);
    if (ticket) startTicketPayment();
    else pay();
  };

  const requestPayment = () => {
    if (!options?.enabled || !selected || !packageAvailable(selected.id) || loading || ticketLoading) return;
    if (!paymentGuideShownRef.current) {
      paymentGuideShownRef.current = true;
      setPaymentGuideOpen(true);
      return;
    }
    if (ticket) startTicketPayment();
    else pay();
  };

  const status = result?.status || order?.status || 'pending';
  const address = order?.qrCode || '';

  return <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-center justify-center px-2 pt-[max(1rem,env(safe-area-inset-top))] sm:p-5">
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeWithoutRecharge} />
    {paymentGuideOpen && <div className="absolute inset-0 z-30 flex items-center justify-center px-4 py-6 sm:px-5">
      <button type="button" aria-label={t('cinema_cancel')} onClick={() => setPaymentGuideOpen(false)} className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative z-10 max-h-full w-full max-w-md overflow-y-auto rounded-2xl border border-amber-500/45 bg-[#160d11] shadow-[0_16px_50px_rgba(0,0,0,0.6)]">
        <div className="flex items-start gap-3 bg-gradient-to-r from-amber-500/20 to-fuchsia-500/10 p-4 sm:p-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-300 sm:h-12 sm:w-12"><i className="ri-shield-flash-fill text-2xl" /></span>
          <div className="min-w-0 pt-0.5">
            <h3 className="text-base font-bold text-foreground-50 sm:text-lg">{t('cinema_rechargeVpnGuideTitle')}</h3>
            <p className="mt-1.5 text-xs leading-5 text-[#D8C2AE] sm:text-sm sm:leading-6">{t('cinema_rechargeVpnWarning')}</p>
          </div>
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-3 py-3 text-left text-xs leading-5 text-[#E3CCB7] sm:px-4 sm:text-sm sm:leading-6">
            <i className="ri-information-line mt-0.5 shrink-0 text-base text-amber-300" />
            <p>{t('cinema_rechargeVpnInstruction')}</p>
          </div>
          <button type="button" onClick={continuePayment} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-pink-500 to-[#F97316] px-3 py-3 text-sm font-bold text-white shadow-[0_8px_24px_rgba(249,115,22,0.16)] hover:brightness-105"><i className="ri-check-line text-lg" />{t('cinema_rechargeVpnContinue')}</button>
        </div>
      </div>
    </div>}
    <div className="relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-background-800 bg-background-900 shadow-2xl sm:max-h-[86dvh] sm:rounded-2xl">
      <header className="flex shrink-0 items-center justify-between px-4 pb-2 pt-3 sm:px-5 sm:pt-4">
        <div className="flex min-w-0 flex-1 items-baseline gap-2 pr-3 sm:gap-3">
          <h2 className="shrink-0 whitespace-nowrap text-lg font-bold text-foreground-50 sm:text-2xl">{t('cinema_rechargeTitle')}</h2>
          <p className="min-w-0 truncate text-xs text-foreground-500 sm:text-sm">{t('cinema_rechargeSubtitle')}</p>
        </div>
        <button onClick={closeWithoutRecharge} aria-label={t('cinema_cancel')} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-foreground-100 sm:h-9 sm:w-9"><i className="ri-close-line text-lg" /></button>
      </header>

      <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 pb-2 sm:px-5 sm:pb-3">
        {!hasPayments && !loading && <div className="rounded-xl border border-background-800 bg-background-950/40 px-4 py-3 text-sm text-foreground-500">{t('cinema_paymentUnavailable')}</div>}
        {hasPayments && <>
          {rechargeBonusActive && <FirstRechargeBonusBanner type={rechargeBonusType as 'first' | 'daily'} percent={rechargeBonusPercent} remainingSeconds={rechargeBonusRemainingSeconds} showPercent={!activityBonusMode} />}
          <div className={`mb-3 grid ${hasCny && hasUsdt ? 'grid-cols-2' : 'grid-cols-1'} gap-1 rounded-lg bg-background-800/70 p-1 sm:rounded-xl`}>
            {hasCny && <button onClick={() => changeTab('cny')} className={`rounded-md px-2 py-2 text-xs font-semibold sm:rounded-lg sm:px-3 sm:text-sm ${tab === 'cny' ? 'bg-[#F19744] text-background-950' : 'text-foreground-500 hover:text-foreground-200'}`}>{t('cinema_rechargeRMB')}</button>}
            {hasUsdt && <button onClick={() => changeTab('usdt')} className={`rounded-md px-2 py-2 text-xs font-semibold sm:rounded-lg sm:px-3 sm:text-sm ${tab === 'usdt' ? 'bg-[#F19744] text-background-950' : 'text-foreground-500 hover:text-foreground-200'}`}>{t('cinema_rechargeUSDT')}</button>}
          </div>
          {packages.length ? <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">{packages.map((item) => {
            const configuredBonusPoints = Math.max(0, Number(item.bonusPoints) || 0);
            const percentageBonusPoints = rechargeBonusActive
              ? Math.floor(item.points * rechargeBonusPercent / 100)
              : 0;
            const bonusPoints = activityBonusMode
              ? (rechargeBonusActive ? (configuredBonusPoints > 0 ? configuredBonusPoints : percentageBonusPoints) : 0)
              : configuredBonusPoints;
            const extraActivityBonusPoints = activityBonusMode ? 0 : percentageBonusPoints;
            const packagePoints = item.points + bonusPoints;
            const selectedForPreview = selectedId === item.id && extraActivityBonusPoints > 0;
            const showCredited = bonusPoints > 0 || selectedForPreview;
            const available = packageAvailable(item.id);
            return <button key={item.id} disabled={!available} onClick={() => { if (available && item.id !== selectedId) { setTicket(null); setSelectedId(item.id); } }} className={`relative min-h-[5rem] rounded-lg border-2 px-3 pb-2.5 text-left sm:min-h-[5.5rem] sm:rounded-xl sm:pb-3 ${bonusPoints > 0 || item.recommended ? 'pt-8' : 'pt-2.5 sm:pt-3'} ${!available ? 'cursor-not-allowed border-background-800 bg-background-950/20 opacity-40' : selectedId === item.id ? 'border-[#F19744] bg-[#F19744]/10' : 'border-background-800 bg-background-950/35 hover:border-background-700'}`}>
              {item.recommended && <span className="absolute left-2 top-2 rounded-full border border-amber-300/30 bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold text-amber-400">{t('cinema_rechargeRecommended')}</span>}
              {bonusPoints > 0 && <span className="absolute right-2 top-2 rounded-full border border-fuchsia-300/40 bg-fuchsia-500/90 px-2 py-0.5 text-[10px] font-extrabold text-white shadow-[0_0_12px_rgba(217,70,239,0.75)]">{t('cinema_rechargeBonus', { points: bonusPoints })}</span>}
              <div className="flex min-w-0 items-start justify-between gap-1.5 sm:gap-2">
                <strong className="truncate text-base text-foreground-50 sm:text-lg">{amountText(item.amount, item.currency)}</strong>
              </div>
              <div className="mt-1.5 flex items-end justify-between gap-2">
                <p className="text-xs text-foreground-400 sm:text-sm">{item.points} {t('cinema_rechargeCoins')}</p>
                <FirstRechargeCreditPreview points={packagePoints} bonusPoints={extraActivityBonusPoints} show={showCredited} animating={!activityBonusMode && bonusAnimatingId === item.id} applied={activityBonusMode || bonusAppliedId === item.id} className="text-[10px] sm:text-xs" />
              </div>
            </button>;
          })}</div> : <div className="mb-3 rounded-lg border border-background-800 px-3 py-3 text-center text-xs text-foreground-500 sm:rounded-xl sm:px-4 sm:text-sm">{t('cinema_rechargeNoPackages')}</div>}
          <p className="mb-1.5 text-xs text-foreground-500">{t('cinema_rechargePayMethod')}</p>
          <div className="mb-2 grid grid-cols-2 gap-2 sm:mb-3">{methods.map((method) => <button key={method.id} onClick={() => { if (method.id !== payMethod) setTicket(null); setPayMethod(method.id as PayMethod); }} className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs sm:gap-2 sm:rounded-xl sm:text-sm ${payMethod === method.id ? 'border-[#F19744] bg-[#F19744]/10 text-foreground-100' : 'border-background-800 bg-background-950/35 text-foreground-500'}`}><i className={`${method.id === 'wechat' ? 'ri-wechat-pay-line text-emerald-400' : method.id === 'usdt' ? 'ri-currency-line text-teal-400' : 'ri-alipay-line text-sky-400'} text-base sm:text-lg`} /><span>{method.label}</span></button>)}</div>
          {selectedMethod && selectedMethod.maxAmount > 0 && <p className="mb-2 text-xs text-amber-400">{selectedMethod.label} 最高支持 ¥{selectedMethod.maxAmount.toFixed(2)}</p>}
        </>}

        {order && <div className="mb-3 rounded-xl border border-background-800 bg-background-950/45 p-3">
          <div className="flex items-start justify-between gap-4"><div><p className="text-xs text-foreground-500">{order.packageTitle}</p><p className="mt-1 text-lg font-bold text-foreground-50">{amountText(order.amount, order.currency)}</p></div><div className="text-right"><p className="text-xs text-foreground-500">{t(`cinema_paymentStatus_${status}`)}</p><p className="mt-1 text-xs text-accent-400">{order.outTradeNo}</p></div></div>
          {order.currency === 'USDT' && address && <div className="mt-3 rounded-lg bg-background-800/60 p-3"><p className="text-[11px] text-foreground-500">{t('cinema_usdtAddress')}</p><p className="mt-1 break-all text-xs text-foreground-200">{address}</p><div className="mt-2 flex justify-between"><span className="text-[11px] text-amber-400">{t('cinema_usdtNetwork')}</span><button onClick={() => void navigator.clipboard?.writeText(address)} className="text-xs text-accent-400">{t('cinema_copy')}</button></div></div>}
          {(order.pageUrl || order.payUrl) && <a href={order.pageUrl || order.payUrl} target="_blank" rel="noopener noreferrer" className="mt-3 flex w-full items-center justify-center rounded-xl bg-[#F19744] py-2.5 text-sm font-bold text-background-950 hover:bg-[#F2A45B]">{t('cinema_rechargePay')}</a>}
        </div>}
        {message && <p className={`mb-3 text-sm ${order ? 'text-amber-400' : 'text-red-400'}`}>{message}</p>}
      </div>

      <footer className="grid shrink-0 grid-cols-2 gap-2 border-t border-background-800 px-4 py-2.5 sm:gap-3 sm:px-5 sm:py-3">
        <button onClick={closeWithoutRecharge} className="rounded-lg border border-background-700 bg-background-800 py-2.5 text-xs font-medium text-foreground-300 hover:bg-background-700 sm:rounded-xl sm:py-3 sm:text-sm">{t('cinema_cancel')}</button>
        {ticket ? <button type="button" onClick={requestPayment} disabled={!hasPayments || !selected || !packageAvailable(selected.id) || loading || ticketLoading} className="disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center rounded-lg bg-[#F19744] py-2.5 text-xs font-bold text-background-950 hover:bg-[#F2A45B] sm:rounded-xl sm:py-3 sm:text-sm">{`${t('cinema_rechargePay')}${selected ? ` ${amountText(selected.amount, selected.currency)}` : ''}`}</button> : <button disabled={!options?.enabled || !selected || !packageAvailable(selected.id) || loading || ticketLoading} onClick={requestPayment} className="rounded-lg bg-[#F19744] py-2.5 text-xs font-bold text-background-950 hover:bg-[#F2A45B] disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-xl sm:py-3 sm:text-sm">{loading || ticketLoading ? t('cinema_loading') : `${t('cinema_rechargePay')}${selected ? ` ${amountText(selected.amount, selected.currency)}` : ''}`}</button>}
      </footer>
    </div>
  </div>;
}
